import {ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../../../../Services/data.service';
import {StorageService} from '../../../../Services/storage.service';
import {Title} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {ReportPrintService} from '../../../../Services/report-print.service';
import {SendEmailModalComponent} from '../../../common/send-email-modal/send-email-modal.component';
import {EmailModel} from '../../../../DataModels/email-model';
import {Modal} from 'bootstrap';
import {DateFilterService} from '../../../../Services/dateFilter.service';

export interface ProjectStatusReportRow {
  project_id: string;
  number: string;
  name: string;
  entity_name: string;
  manager_user_id: string;
  status: string;
  billing_type: string;
  start_date: string;
  due_date: string;
  completed_date: string | null;
  budget_hours: number;
  actual_hours: number;
  budget_amount: number;
  percent_complete: number;
  hours_variance: number;
}

@Component({
  selector: 'app-project-status-report',
  templateUrl: './project-status-report.component.html',
  styleUrls: [],
  standalone: false,
})
export class ProjectStatusReportComponent implements OnInit {

  @ViewChild('ReportOutput', {static: false}) ReportOutput: ElementRef;
  @ViewChild(SendEmailModalComponent, {read: ElementRef}) private localSendEmailModalHost: ElementRef;

  rptName = 'Project Status Summary';
  isLoading = false;
  noDataFound = false;
  rows: ProjectStatusReportRow[] = [];
  users: any[] = [];
  reportDataToSendInEmail: EmailModel = new EmailModel();

  buttonConfig: any = {
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: false,
    close: true,
    clear: false,
    filters: false,
    reset: true,
  };

  filters: any = {
    company_id: '',
    status: '',
    billing_type: '',
    manager_user_id: '',
    from_date: '',
    to_date: '',
  };


  statusOptions = ['', 'Draft', 'Active', 'On Hold', 'Completed', 'Cancelled'];
  billingTypeOptions = [
    {value: '', label: '— All —'},
    {value: 'TimeAndMaterials', label: 'Time & Materials'},
    {value: 'PercentageComplete', label: 'Percentage Complete'},
    {value: 'ByTaskPercentageComplete', label: 'By Task % Complete'},
    {value: 'FixedPrice', label: 'Fixed Price'},
    {value: 'NonBillable', label: 'Non-Billable'},
  ];

  vm: any = {
    Preferences: [],
  };

  constructor(
    private titleService: Title,
    private router: Router,
    private tableDataService: DataService,
    private localStorage: StorageService,
    private notification: BsToastService,
    private reportPrint: ReportPrintService,
    private cd: ChangeDetectorRef,
    private dateFilter: DateFilterService,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle(this.rptName);
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.filters.company_id = this.vm.Preferences?.defaults?.company_id ?? '';

    this.tableDataService.getApiListData('sys_user', '?pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.users = result.Data ?? result.items ?? [];
      },
    });
  }

  getUserName(userId: string): string {
    if (!userId) {
      return '—';
    }
    const u = this.users.find(x => x.id === userId);
    return u ? u.name : userId;
  }

  percentClass(pct: number): string {
    if (pct > 100) {
      return 'text-danger fw-bold';
    }
    if (pct >= 80) {
      return 'text-warning fw-bold';
    }
    return 'text-success';
  }

  varianceClass(v: number): string {
    return v > 0 ? 'text-danger' : 'text-success';
  }

  resetFilters() {
    this.filters = {
      company_id: this.vm.Preferences?.defaults?.company_id ?? '',
      status: '',
      billing_type: '',
      manager_user_id: '',
      from_date: '',
      to_date: '',
    };
    this.rows = [];
    this.vm.report = [];
  }

  close() {
    this.router.navigate(['/home']);
  }

  runReport() {
    this.isLoading = true;
    this.noDataFound = false;
    this.rows = [];

    const params: string[] = [];
    if (this.filters.company_id) {
      params.push('company_id=' + this.filters.company_id);
    }
    if (this.filters.status) {
      params.push('status=' + this.filters.status);
    }
    if (this.filters.billing_type) {
      params.push('billing_type=' + this.filters.billing_type);
    }
    if (this.filters.manager_user_id) {
      params.push('manager_user_id=' + this.filters.manager_user_id);
    }
    const fromDate = this.filters.from_date ? this.dateFilter.GetDateFormat(this.filters.from_date) : '';
    const toDate = this.filters.to_date ? this.dateFilter.GetDateFormat(this.filters.to_date) : '';
    if (fromDate) {
      params.push('from_date=' + fromDate);
    }
    if (toDate) {
      params.push('to_date=' + toDate);
    }

    this.tableDataService.getDataWithParams('ProjectStatusReport', params.join('&')).subscribe({
      next: (result: any) => {
        this.rows = Array.isArray(result) ? result : [];
        this.vm.report = this.rows;
        this.noDataFound = this.rows.length === 0;
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast('Error loading report.');
        this.isLoading = false;
      },
    });
  }

  printReport() {
    try {
      const html = this.buildPrintHtml();
      this.reportPrint.printInIframe(html);
    } catch (e) {
      console.error('Project Status Report print failed', e);
    }
  }

  setEmailData() {
    const emailDocObj = {} as EmailModel;
    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '';
    emailDocObj.toEmail = '';
    emailDocObj.body = 'Please see the attached ' + this.rptName + '.<br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + this.rptName;
    try {
      this.cd.detectChanges();
    } catch {
    }
    try {
      emailDocObj.pdfHtml = this.buildPrintHtml(true);
    } catch {
      emailDocObj.pdfHtml = this.ReportOutput?.nativeElement?.outerHTML || '';
    }
    emailDocObj.activity_message = this.rptName;
    const now = new Date();
    emailDocObj.FileName = `ProjectStatusSummary_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.pdf`;
    this.reportDataToSendInEmail = emailDocObj;
    const element = document.getElementById('sendEmailModal') as HTMLElement;
    if (element) {
      const emailModal = new Modal(element);
      emailModal.show();
    }
  }

  private buildPrintHtml(forEmail: boolean = false): string {
    const rawContent = (this.ReportOutput?.nativeElement?.outerHTML || '').toString();
    const content = this.reportPrint.cleanContentHtml(rawContent);
    const meta: { label: string; value: string }[] = [];
    if (this.filters.from_date || this.filters.to_date) {
      meta.push({label: 'Date Range', value: [this.filters.from_date, this.filters.to_date].filter(Boolean).join(' to ')});
    }
    if (this.filters.status) {
      meta.push({label: 'Status', value: this.filters.status});
    }
    if (this.filters.billing_type) {
      meta.push({label: 'Billing Type', value: this.filters.billing_type});
    }
    return this.reportPrint.buildHtml({title: this.rptName, contentHtml: content, meta, orientation: 'landscape'});
  }
}
